import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Landmark } from 'lucide-react';
import s from './BrokerProgram.module.sass';

// Премиум иконки статусов в стиле Wise/Revolut
const StatusIcon = ({ status }) => {
    if (status === 'Success') {
        return (
            <div className={s.statusIconWrapper}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="16" fill="#10B981" fillOpacity="0.1"/>
                    <circle cx="16" cy="16" r="12" fill="#10B981"/>
                    <path d="M11 16L14 19L21 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
        );
    }
    if (status === 'In Process') {
        return (
            <div className={s.statusIconWrapper}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="16" fill="#F59E0B" fillOpacity="0.1"/>
                    <circle cx="16" cy="16" r="12" fill="#F59E0B"/>
                    <circle cx="16" cy="12" r="1.5" fill="white">
                        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="16" cy="16" r="1.5" fill="white">
                        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" begin="0.5s" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="16" cy="20" r="1.5" fill="white">
                        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" begin="1s" repeatCount="indefinite"/>
                    </circle>
                </svg>
            </div>
        );
    }
    return (
        <div className={s.statusIconWrapper}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="16" fill="#3B82F6" fillOpacity="0.1"/>
                <circle cx="16" cy="16" r="12" fill="#3B82F6"/>
                <g>
                    <animateTransform attributeName="transform" type="rotate" values="0 16 16;360 16 16" dur="2s" repeatCount="indefinite"/>
                    <path d="M16 10V16L20 20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
            </svg>
        </div>
    );
};

export const TransactionCard = ({
    name,
    amount,
    bank,
    bankIconSrc,
    digits,
    status,
    currency,
    date,
    dateObj,
    txId,
    onClick,
    onChangeStatus,
    translateStatus,
    siteSettings,
    editable = false,
    editFields = {},
    banks = [],
    onEditField,
}) => {
    // Что именно можно править (по умолчанию всё включено, если главный тумблер вкл)
    const canEdit = (field) => editable && editFields[field] !== false;
    // Одноразовое редактирование строки по двойному клику (нигде не сохраняется).
    // Активируется только когда editable === true (настройка конфига).
    // Правится имя, банк (дропдаун), последние 4 цифры, сумма, время и хэш.
    const [editing, setEditing] = useState(null); // 'name' | 'bank' | 'digits' | 'amount' | 'txId' | 'time' | null
    const [editVal, setEditVal] = useState('');
    // Закрываем дропдаун банка по клику вне него — в т.ч. когда открывают
    // дропдаун в другой карточке (у каждой карточки своё состояние editing)
    const bankWrapRef = useRef(null);
    useEffect(() => {
        if (editing !== 'bank') return undefined;
        const onDocMouseDown = (e) => {
            if (bankWrapRef.current && !bankWrapRef.current.contains(e.target)) {
                setEditing(null);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [editing]);

    // Выделение текста мышкой часто заканчивается ЗА краем поля: click тогда
    // прилетает в родителя и закрывал редактор. Запоминаем, где начался
    // mousedown: если внутри редактора — click по карточке игнорируем.
    const editBoxRef = useRef(null);
    const pressStartedInEditorRef = useRef(false);
    useEffect(() => {
        if (!editing || editing === 'bank') return undefined;
        const onDown = (e) => {
            pressStartedInEditorRef.current = !!(editBoxRef.current && editBoxRef.current.contains(e.target));
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, [editing]);

    // 24-часовое HH:MM из даты — стабильный префилл для правки времени
    const to24h = (d) => {
        const x = new Date(d);
        return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
    };
    const stop = (e) => e.stopPropagation();
    const startEdit = (field, current, e) => {
        if (!canEdit(field)) return;
        e?.stopPropagation();
        setEditVal(current == null ? '' : String(current));
        setEditing(field);
    };
    const commit = () => {
        if (!editing) return;
        onEditField?.(editing, editVal);
        setEditing(null);
        setEditVal('');
    };
    const cancel = () => { setEditing(null); setEditVal(''); };
    const commitBank = (val) => { onEditField?.('bank', val); setEditing(null); };
    const onEditKey = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    };
    const bankIconOf = (b) => {
        const f = siteSettings?.banks?.[b]?.iconFileName;
        return f ? `/bankicons/${f}` : null;
    };
    // Инлайн-редактор текстового поля: аккуратное поле + кнопка «применить»
    const InlineEdit = ({ inputClass, onChangeValue = (v) => v, inputProps }) => (
        <span className={s.inlineEdit} ref={editBoxRef} onClick={stop}>
            <input
                type="text"
                className={inputClass}
                value={editVal}
                autoFocus
                onChange={(e) => setEditVal(onChangeValue(e.target.value))}
                onKeyDown={onEditKey}
                {...inputProps}
            />
            <button
                type="button"
                className={s.inlineApply}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => { e.stopPropagation(); commit(); }}
                aria-label="Применить"
            >
                <Check size={13} strokeWidth={2.6} aria-hidden="true" />
            </button>
        </span>
    );
    // Фолбэк, если txId не передан (но обычно приходит из данных родителя и стабилен)
    const ensuredTxId = txId || (() => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = 'TX';
        for (let i = 0; i < 6; i++) { result += chars.charAt(Math.floor(Math.random() * chars.length)); }
        return result;
    })();
    
    // Получаем флаг страны из настроек
    const getFlagImage = () => {
        try {
            const flagCode = siteSettings?.selectedFlag || 'my';
            return require(`../../img/flagicons/${flagCode}.svg`).default || require(`../../img/flagicons/${flagCode}.svg`);
        } catch (error) {
            console.warn('Flag not found:', siteSettings?.selectedFlag);
            try {
                return require(`../../img/flagicons/my.svg`).default || require(`../../img/flagicons/my.svg`);
            } catch (fallbackError) {
                return null;
            }
        }
    };
    
    const countryFlag = getFlagImage();
    
    // Получаем класс статуса для применения к карточке
    const statusClass = status.toLowerCase().replace(' ', '');

    return (
        <div className={`${s.compactTxCard} ${s[`status-${statusClass}`]}`}>
            {/* КОМПАКТНАЯ СЕТКА: все в одной строке */}
            <div className={s.txGrid}>
                {/* Левая зона: Статус + Инфо */}
                <div className={s.leftZone}>
                    <StatusIcon status={status} />
                    <div className={s.mainInfo}>
                        <div className={s.nameRow}>
                            {canEdit('name') && editing === 'name' ? (
                                InlineEdit({ inputClass: s.nameEdit })
                            ) : (
                                <span
                                    className={s.name}
                                    onClick={canEdit('name') ? stop : undefined}
                                    onDoubleClick={canEdit('name') ? (e) => startEdit('name', name, e) : undefined}
                                >
                                    {name}
                                </span>
                            )}
                        </div>
                        <div className={s.metaRow}>
                            {canEdit('txId') && editing === 'txId' ? (
                                InlineEdit({ inputClass: s.metaEdit })
                            ) : (
                                <span
                                    className={s.txIdCompact}
                                    onClick={canEdit('txId') ? stop : undefined}
                                    onDoubleClick={canEdit('txId') ? (e) => startEdit('txId', ensuredTxId, e) : undefined}
                                >#{ensuredTxId}</span>
                            )}
                            {/* Точку-разделитель и время рисуем только когда время
                                реально есть (в конфиге может быть showDateTime: false) */}
                            {(() => {
                                const timeStr = (date || '').split(' ')[1] || '';
                                const isEditingTime = canEdit('time') && editing === 'time';
                                if (!timeStr && !isEditingTime) return null;
                                return (
                                    <>
                                        <span className={s.dot}>•</span>
                                        {isEditingTime ? (
                                            InlineEdit({ inputClass: s.metaEdit })
                                        ) : (
                                            <span
                                                className={s.timeOnly}
                                                onClick={canEdit('time') ? stop : undefined}
                                                onDoubleClick={canEdit('time') ? (e) => startEdit('time', to24h(dateObj), e) : undefined}
                                            >{timeStr}</span>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* Центральная зона: Банк + Метод */}
                <div className={s.centerZone}>
                    <div className={s.bankRow}>
                        {canEdit('bank') && editing === 'bank' ? (
                            <span className={s.bankSelectWrap} ref={bankWrapRef} onClick={stop}>
                                <button
                                    type="button"
                                    className={s.bankSelectBtn}
                                    onClick={(e) => { e.stopPropagation(); cancel(); }}
                                    aria-expanded="true"
                                >
                                    {bankIconSrc && <img className={s.miniBank} src={bankIconSrc} alt="" />}
                                    <span className={s.bankSelectValue}>{bank}</span>
                                    <ChevronDown size={13} strokeWidth={2} className={`${s.bankSelectChevron} ${s.bankSelectChevronOpen}`} aria-hidden="true" />
                                </button>
                                <div className={s.bankDropdown}>
                                    {(banks || []).map((b) => {
                                        const ic = bankIconOf(b);
                                        return (
                                            <button
                                                type="button"
                                                key={b}
                                                className={`${s.bankOption} ${b === bank ? s.bankOptionSelected : ''}`}
                                                onClick={(e) => { e.stopPropagation(); commitBank(b); }}
                                            >
                                                {ic
                                                    ? <span className={s.bankOptionPlate}><img src={ic} alt="" /></span>
                                                    : <span className={`${s.bankOptionPlate} ${s.bankOptionPlateEmpty}`}><Landmark size={12} strokeWidth={2} aria-hidden="true" /></span>}
                                                <span className={s.bankOptionName}>{b}</span>
                                                {b === bank && <Check size={14} strokeWidth={2.4} className={s.bankOptionCheck} aria-hidden="true" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </span>
                        ) : (
                            <>
                                {bankIconSrc && <img className={s.miniBank} src={bankIconSrc} alt={bank} />}
                                <span
                                    className={s.bankText}
                                    onClick={canEdit('bank') ? stop : undefined}
                                    onDoubleClick={canEdit('bank') ? (e) => startEdit('bank', bank, e) : undefined}
                                >{bank}</span>
                            </>
                        )}
                    </div>
                    <div className={s.methodRow}>
                        <div className={s.methodWithFlag} onClick={() => {
                            // Выделение начали в поле, а отпустили за краем — не закрываем
                            if (pressStartedInEditorRef.current) { pressStartedInEditorRef.current = false; return; }
                            if (editing) { cancel(); return; }
                            onClick?.();
                        }}>
                            <span className={s.method}>Bank Transfer</span>
                            {countryFlag && <img className={s.flag} src={countryFlag} alt="Flag" />}
                        </div>
                        <span className={s.account}>••••{canEdit('digits') && editing === 'digits' ? (
                            InlineEdit({
                                inputClass: s.digitsEdit,
                                onChangeValue: (v) => v.replace(/\D/g, '').slice(0, 4),
                                inputProps: { maxLength: 4, inputMode: 'numeric' },
                            })
                        ) : (
                            <span
                                onClick={canEdit('digits') ? stop : undefined}
                                onDoubleClick={canEdit('digits') ? (e) => startEdit('digits', digits, e) : undefined}
                            >{digits}</span>
                        )}</span>
                    </div>
                </div>

                {/* Элегантная стрелочка между центральной и правой зоной */}
                <div className={`${s.arrowSeparator} ${s[status.toLowerCase().replace(' ', '')]}`}>
                    <svg width="24" height="12" viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                            d="M1 6H22M22 6L17 1M22 6L17 11" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                {/* Правая зона: ТОЛЬКО СУММА + Статус */}
                <div className={s.rightZone}>
                    <div className={s.amountContainer}>
                        <span className={s.currency}>{currency}</span>
                        {canEdit('amount') && editing === 'amount' ? (
                            InlineEdit({ inputClass: s.amountEdit })
                        ) : (
                            <span
                                className={s.amountBig}
                                onClick={canEdit('amount') ? stop : undefined}
                                onDoubleClick={canEdit('amount') ? (e) => startEdit('amount', amount, e) : undefined}
                            >{amount}</span>
                        )}
                    </div>
                    <div 
                        className={`${s.statusDot} ${s[status.toLowerCase().replace(' ', '')]}`}
                        onClick={(e) => { e.stopPropagation(); onChangeStatus(); }}
                    >
                        {translateStatus(status)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionCard;


